import { lazy, ReactNode, Suspense, useEffect, useState } from "react";

import {
  AdminBanner,
  CreateBannerPayload,
  UpdateBannerPayload,
} from "~/api/banner.types";
import { Chip, Modal, Text } from "~/ui/common";
import { BannerPreview } from "./banner-preview";

const RichTextEditor = lazy(
  () => import("~/ui/form/rich-text/rich-text-editor"),
);

interface BannerFormModalProps {
  show: boolean;
  banner?: AdminBanner;
  readOnly?: boolean;
  titleAdornment?: ReactNode;
  onClose: () => void;
  onSubmit: (data: CreateBannerPayload | UpdateBannerPayload) => void;
  isLoading: boolean;
}

export const BannerFormModal = ({
  show,
  banner,
  readOnly = false,
  onClose,
  onSubmit,
  isLoading,
}: BannerFormModalProps) => {
  const [content, setContent] = useState(banner?.content ?? "");

  useEffect(() => {
    if (show) {
      setContent(banner?.content ?? "");
    }
  }, [show, banner?.content]);

  const title = readOnly
    ? "View Banner"
    : banner
      ? "Edit banner"
      : "Create banner";

  return (
    <Modal
      show={show}
      title={title}
      titleAdornment={
        <Chip
          type={banner?.is_active ? "success" : "neutral"}
          className="text-base"
        >
          {banner?.is_active ? "Active" : "Inactive"}
        </Chip>
      }
      onClose={onClose}
      handleConfirm={readOnly ? undefined : () => onSubmit({ content })}
      confirmLabel={banner ? "Save" : "Create"}
      isLoading={isLoading}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Text>Content</Text>
          {readOnly ? (
            <BannerPreview content={content} />
          ) : (
            <Suspense
              fallback={
                <div className="h-32 animate-pulse rounded-md bg-gray-100" />
              }
            >
              <RichTextEditor value={content} onChange={setContent} />
            </Suspense>
          )}
        </div>

        {!readOnly && content && (
          <div className="flex flex-col gap-1">
            <Text>Preview</Text>
            <BannerPreview content={content} />
          </div>
        )}
      </div>
    </Modal>
  );
};
